/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, IAny, ICreateActionContext, IGeneric } from '@msdyn365-commerce/core';
import { FeatureState } from '@msdyn365-commerce/retail-proxy';
import { getDeliveryOptionsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { FilterDeliveryModeOption, ProductDeliveryOptions, ReleasedProductType, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { getSelectedProductIdFromActionInput, getSelectedVariant, SelectedVariantInput } from '.';
import { FeatureStateInput, getFeatureStateActionDataAction } from './get-feature-state';

/**
 * Input class for the GetDeliveryOptionsForSelectedVariant Data Action.
 */
export class GetDeliveryOptionsForSelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public selectedProduct: SimpleProduct | undefined;

    public customerId: string;

    public featureState?: boolean;

    constructor(productId: number, channelId: number, selectedProduct?: SimpleProduct, customerId?: string, featureState?: boolean) {
        this.productId = productId;
        this.channelId = channelId;
        this.selectedProduct = selectedProduct;
        this.customerId = customerId || '';
        this.featureState = featureState;
    }

    public getCacheKey = () => 'DeliveryOptionsForSelectedVariant';

    public getCacheObjectType = () => 'DeliveryOptions';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * Calls the Retail Feature State API and returns a list of feature with isEnabled flag.
 * @param context
 */
export async function getFeatureState(context: IActionContext): Promise<FeatureState[]> {
    return getFeatureStateActionDataAction(new FeatureStateInput(), context);
}

/**
 * The createInput method for the GetDeliveryOptionsForSelectedVariant Data Action.
 * @param inputData The input data for the createInput method.
 */
export const createDeliveryOptionsForSelectedVariantInput = (inputData: ICreateActionContext<IGeneric<IAny>>): GetDeliveryOptionsForSelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);

    if (productId) {
        return new GetDeliveryOptionsForSelectedVariantInput(
            +productId,
            +inputData.requestContext.apiSettings.channelId,
            undefined,
            undefined
        );
    }
    throw new Error('Unable to create PriceForSelectedVariantInput, no productId found on module config or query');

};

/**
 * The Action Method for the GetDeliveryOptionsForSelectedVariant Data Action
 * Pulls the currently selected variant from the cache using the getSelectedVariant data action, and gets it's supported delivery options
 * via the getDeliveryOptionsAsync RetailServer API.
 * @param input
 * @param ctx
 */
export async function getDeliveryOptionsForSelectedVariantAction(
    input: GetDeliveryOptionsForSelectedVariantInput,
    ctx: IActionContext
): Promise<ProductDeliveryOptions | undefined> {
    if (input.featureState === undefined) {
        const featureState = await getFeatureState(ctx);
        input.featureState = featureState?.find(item => item.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature')?.IsEnabled;
    }

    return Promise.resolve()
        .then(() => {
            const activeProduct: SimpleProduct | undefined = input.selectedProduct;

            if (!activeProduct) {
                const selectedVariantInput = new SelectedVariantInput(input.productId, input.channelId);

                return getSelectedVariant(selectedVariantInput, ctx);
            }

            return activeProduct;
        })
        .then((productResult: SimpleProduct | null) => {
            const activeProduct: SimpleProduct | undefined = <SimpleProduct | undefined>productResult;

            if (activeProduct) {
                return getDeliveryOptionsAsync(
                    { callerContext: ctx, queryResultSettings: {} },
                    [activeProduct.RecordId],
                    {},

                    // For service item, should not apply the filter, otherwise there will be no electronic delivery mode
                    (input.featureState && productResult?.ItemTypeValue !== ReleasedProductType.Service) ? FilterDeliveryModeOption.FilterOutNonPickupDeliveryMode : null
                ).then(response => {
                    if (response && response.length > 0) {
                        return response[0];
                    }

                    return undefined;
                });
            }

            return undefined;
        })
        .catch((error: Error) => {
            ctx.trace(error.message);
            ctx.telemetry.exception(error);
            ctx.telemetry.debug('[getDeliveryOptionsForSelectedVariantAction]Error executing action');
            throw new Error('[getDeliveryOptionsForSelectedVariantAction]Error executing action');
        });
}

export const getDeliveryOptionsForSelectedVariantActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-delivery-options-for-selected-variant',
    action: <IAction<ProductDeliveryOptions | undefined>>getDeliveryOptionsForSelectedVariantAction,
    input: createDeliveryOptionsForSelectedVariantInput
});

export default getDeliveryOptionsForSelectedVariantActionDataAction;
